import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Modal } from '@mui/material';
import muteIcon from 'packages/novous_web/src/assets/icons/Mute.svg';
import unmuteIcon from 'packages/novous_web/src/assets/icons/unMute.svg';
import endCallIcon from 'packages/novous_web/src/assets/icons/End Call.svg';
import avatar from 'packages/novous_web/src/assets/icons/avatar.svg';
import { API_ENDPOINTS } from '../../api/endpoints';
import useAxios from '../../api/useAxios';
import AlertToast from './Alert';
import { WebRTCCallService } from '../../service/webRTCCall.service';
import { EmqxCallService, resolveEmqxConfig } from '../../service/emqxCall.service';
import { IAwsCredentials } from '../../types/kinesis';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';

interface CallingModalProps {
  open: boolean;
  onClose: () => void;
  tripInfo: any;
}

const CallingModal: React.FC<CallingModalProps> = ({ open, onClose, tripInfo }) => {
  const [toast, setToast] = useState({ open: false, message: '', type: 'error' });
  const [connectionState, setConnectionState] = useState<
    'connecting' | 'connected' | 'disconnected' | 'closed'
  >('disconnected');
  const [isMuted, setIsMuted] = useState(false);
  const webrtc = useRef<WebRTCCallService | EmqxCallService | null>(null);
  const connectionStatus = useRef(-1);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [callDuration, setCallDuration] = useState(0);

  const [position, setPosition] = useState({
    x: window.innerWidth / 2 - 220,
    y: window.innerHeight / 2 - 204,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const modalRef = useRef<HTMLDivElement>(null);
  const networkPollRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (modalRef.current) {
      const rect = modalRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;

        // Boundary checking to keep modal within viewport
        const boundedX = Math.max(0, Math.min(newX, window.innerWidth - 440));
        const boundedY = Math.max(0, Math.min(newY, window.innerHeight - 408));

        setPosition({
          x: boundedX,
          y: boundedY,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const connectingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    data: vehicleCallingData,
    loading: vehicleCallingLoading,
    fetchData: fetchVehicleCallingData,
    error: vehicleCallingError,
    errorStatus: vehicleCallingErrorStatus,
  } = useAxios<{ data: any; message: string }>(
    `${API_ENDPOINTS.dashboard.CALLING}${tripInfo?.vehicleRegNo || tripInfo?.vehicleId}`,
    'GET',
  );

  const { data: vehicleStopCallingData, fetchData: fetchVehicleStopCallingData } = useAxios(
    `${API_ENDPOINTS.dashboard.STOP_CALLING}${tripInfo?.vehicleRegNo || tripInfo?.vehicleId}`,
    'GET',
  );

  const [networkStrength, setNetworkStrength] = useState<string>('NA');

  useEffect(() => {
        if (open && tripInfo) {
          const currentVehicleId = tripInfo?.vehicleRegNo || tripInfo?.vehicleId;
          console.log("Setting up WebSocket for vehicle ID:", currentVehicleId);

      if (!currentVehicleId) return;

      const socket = new SockJS(`${process.env.NX_PUBLIC_API_URL}/ws`);
      const stompClient = new Client({
        webSocketFactory: () => socket,
        onConnect: () => {
          console.log("WebSocket connected, subscribing to signals topic");
          stompClient.subscribe(`/topic/signals/${currentVehicleId}`, (message: { body: string }) => {
            try {
              const parsedMessage = JSON.parse(message.body);
              console.log("Received signal message:", parsedMessage);
              setNetworkStrength(parsedMessage.signalStrength || 'NA');
            } catch (error) {
              console.error("Error parsing WebSocket message:", error);
            }
          });
        },
        onStompError: (frame) => {
          console.error('WebSocket error:', frame);
        },
        onDisconnect: () => {
          console.log('WebSocket disconnected');
        }
      });

      stompClient.activate();

      return () => {
        console.log("🧹 Cleaning up WebSocket connection");
        stompClient.deactivate();
      };
    }
  }, [open, tripInfo]);

  const getNetworkStyle = (strength: string) => {
    const normalized = strength?.trim().toLowerCase();
    switch (normalized) {
      case 'excellent':
        return { bgColor: 'rgba(39, 174, 96, 0.15)', textColor: '#219653' }; // Green
      case 'medium':
        return { bgColor: 'rgba(255, 193, 7, 0.15)', textColor: '#FFC107' }; // Yellow
      case 'low':
        return { bgColor: 'rgba(244, 67, 54, 0.15)', textColor: '#F44336' }; // Red
      default:
        return { bgColor: 'rgba(189, 189, 189, 0.15)', textColor: '#9E9E9E' }; // Grey
    }
  };

  useEffect(() => {
    if (vehicleStopCallingData) {
      if (networkPollRef.current) {
        clearInterval(networkPollRef.current);
        networkPollRef.current = null;
      }
      onClose();
    }
  }, [vehicleStopCallingData]);

  const strength = networkStrength
  const { bgColor, textColor } = getNetworkStyle(strength);

  useEffect(() => {
    if (open && tripInfo) {
      connectionStatus.current = 0;

      setConnectionState('connecting');
      fetchVehicleCallingData();
    }
  }, [tripInfo, open]);

  useEffect(() => {
    if (vehicleStopCallingData) {
      // endCall();
      onClose();
    }
  }, [vehicleStopCallingData]);

  useEffect(() => {
    if (vehicleCallingErrorStatus === '500' && !vehicleCallingLoading && open) {
      setToast({ open: true, message: vehicleCallingError || '500 error code', type: 'error' });
      onClose();
    }
  }, [fetchVehicleCallingData, vehicleCallingErrorStatus]);

  useEffect(() => {
    const initiateCall = async () => {
      if (!vehicleCallingData?.data) return;

      // if (vehicleCallingData?.message == 'Device Is not Online') {
      //   setToast({ open: true, message: 'Device is Offline', type: 'error' });
      //   onClose();
      // }

      const hasMicAccess = await checkMicrophoneAccess();
      if (!hasMicAccess) {
        setToast({
          open: true,
          message: 'Microphone is blocked',
          type: 'error',
        });

        handleCallEnd();
        return;
      }

      const emqxConfig = resolveEmqxConfig(vehicleCallingData.data);
      if (emqxConfig) {
        const emqxCall = new EmqxCallService();
        webrtc.current = emqxCall;
        try {
          await emqxCall.startCall(audioRef, emqxConfig, handleAudioCallStatus);
        } catch (err: any) {
          console.error('[EMQX] Call Error', err);
          setToast({ open: true, message: 'Connection Error: ' + err?.message, type: 'error' });
          handleCallEnd();
        }
        return;
      }

      const awsCredentials: IAwsCredentials = {
        credentials: {
          accessKeyId: vehicleCallingData.data.accessKeyId,
          secretAccessKey: vehicleCallingData.data.secretKeyId,
          sessionToken: vehicleCallingData.data.sessionToken,
        },
        region: 'ap-south-1',
      };

      webrtc.current = new WebRTCCallService(awsCredentials);

      webrtc.current.startCall(
        audioRef,
        handleAudioConnection,
        vehicleCallingData?.data.channelArn,
        handleAudioCallStatus,
        (data: any, status: string, topicKey: string) => { },
        // handleCallStatus
      );
    };

    if (vehicleCallingData?.data) {
      initiateCall();
    }

    return () => {
      clearInterval(timerRef.current!);
    };
  }, [vehicleCallingData]);

  const handleAudioConnection = (status: boolean) => {
    // console.info(status, "handleAudioConnecttion");
  };

  const handleCallStatus = (status: string) => {
    if (status == 'disconnected') setConnectionState('disconnected');
  };

  const handleAudioCallStatus = (status: boolean) => {
    if (status) {
      setConnectionState('connected');
      connectionStatus.current = 1;
      // Clear any existing interval (just to be safe)
      clearInterval(timerRef.current!);

      // Start the timer
      timerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    }
  };

  useEffect(() => {
    if (connectionState === 'connecting' && open) {
      setTimeout(() => { }, 30000);
      connectingTimeoutRef.current = setTimeout(() => {
        if (connectionStatus.current !== 1) {
          setToast({
            open: true,
            message: 'Call could not connect. Please try again.',
            type: 'error',
          });
          handleCallEnd();
        }
      }, 60000);
    } else if (connectionState === 'connected') {
      if (connectingTimeoutRef.current) {
        clearTimeout(connectingTimeoutRef.current);
        connectingTimeoutRef.current = null; // Clear the reference after clearing timeout
      }
    }

    return () => {
      clearTimeout(connectingTimeoutRef.current!);
      connectingTimeoutRef.current = null;
    };
  }, [connectionState]);

  const handleMuteToggle = () => {
    const newMuteState = !isMuted;
    if (webrtc.current) {
      webrtc.current.toggleAudioMute((muted) => { });
      setIsMuted(newMuteState);
    }
  };

  const handleCallEnd = () => {
    clearInterval(timerRef.current!);
    clearTimeout(connectingTimeoutRef.current!);
    onClose();
    if (webrtc.current) {
      webrtc.current.endCall(handleAudioCallStatus, (data: any, status: string, topicKey: string) => { });
    }
    setCallDuration(0);
    fetchVehicleStopCallingData();
    setConnectionState('closed');

    connectionStatus.current = -1;
  };

  const checkMicrophoneAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (err) {
      return false;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <Modal
        open={open && connectionState !== 'closed'}
        onClose={handleCallEnd}
        BackdropProps={{ style: { backgroundColor: 'transparent' } }}
        hideBackdrop
      >
        <Box
          className="calling-modal"
          ref={modalRef}
          sx={{
            width: 440,
            height: 408,
            bgcolor: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid #E0F7F8',
            boxShadow: 24,
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'absolute',
            top: `${position.y}px`,
            left: `${position.x}px`,
            cursor: isDragging ? 'grabbing' : 'grab',
            zIndex: 9999,
            userSelect: 'none',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
          onMouseDown={handleMouseDown}
        >
          <audio ref={audioRef} />
          <div className="flex gap-1 flex-col w-full">
            <div className="flex gap-2 items-center justify-between">
              <Typography variant="h6" fontWeight={600}>
                {tripInfo?.driverName || 'NA'}
              </Typography>
              <div className="flex items-center gap-1">
                <Typography variant="caption" color="secondary">
                  Network
                </Typography>
                <Box
                  sx={{
                    backgroundColor: bgColor,
                    px: '8px',
                    py: '4px',
                    borderRadius: '8px',
                    display: 'inline-block',
                  }}
                >
                  <Typography variant="overline" color={textColor} fontWeight={400}>
                    {strength}
                  </Typography>
                </Box>
              </div>
            </div>
            <Typography variant="button" fontWeight={400}>
              {tripInfo?.vehicleRegNo || tripInfo?.vehicleId || 'NA'}
            </Typography>
          </div>

          <img src={avatar} alt="avatar" height={120} width={120} />

          <Typography variant="button">
            {connectionState === 'connected'
              ? 'Connected'
              : connectionState === 'connecting'
                ? 'Connecting...'
                : connectionState === 'disconnected'
                  ? 'Disconnected'
                  : 'Calling...'}
          </Typography>
          <Typography variant="button" className="mt-1">
            {connectionState === 'connected' && `Call Duration: ${formatTime(callDuration)}`}
          </Typography>

          <div className="flex gap-6 justify-center">
            <img
              src={!isMuted ? muteIcon : unmuteIcon}
              alt="mute"
              height={48}
              width={48}
              onClick={handleMuteToggle}
              style={{
                cursor: connectionState !== 'connected' ? 'not-allowed' : 'pointer',
                opacity: connectionState !== 'connected' ? 0.5 : 1,
              }}
            />

            <img
              src={endCallIcon}
              alt="end call"
              height={48}
              width={48}
              onClick={handleCallEnd}
              style={{ cursor: 'pointer' }}
            />
          </div>
        </Box>
      </Modal>

      <AlertToast
        open={toast.open}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        type={toast.type as any}
      />
    </>
  );
};

export default CallingModal;
